import { ExternalLink, CheckCircle2, AlertTriangle, AlertOctagon, ListChecks } from "lucide-react";
import {
  MANUAL_TITLE,
  MANUAL_INTRO,
  MANUAL_SECTIONS,
} from "../lib/manualContent";

function ManualLink({ href, children }) {
  // Internal app links open in the current tab; external ones in a new tab.
  const isExternal = /^https?:\/\//i.test(href) && !href.startsWith(window.location.origin);
  if (isExternal) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-slate-900 font-medium underline decoration-slate-400 decoration-1 underline-offset-[3px] hover:decoration-slate-900 hover:decoration-2 transition-[text-decoration-color]"
      >
        {children}
        <ExternalLink
          className="inline-block w-3 h-3 ml-0.5 -translate-y-px text-slate-500"
          strokeWidth={2}
        />
      </a>
    );
  }
  return (
    <a
      href={href}
      className="text-slate-900 font-medium underline decoration-slate-400 decoration-1 underline-offset-[3px] hover:decoration-slate-900 hover:decoration-2 transition-[text-decoration-color]"
    >
      {children}
    </a>
  );
}

function renderInline(parts) {
  return parts.map((part, i) =>
    part.href ? (
      <ManualLink key={i} href={part.href}>
        {part.text}
      </ManualLink>
    ) : (
      <span key={i}>{part.text}</span>
    ),
  );
}

function Paragraph({ block }) {
  return (
    <p className="text-[15px] leading-7 text-slate-700 mb-4 last:mb-0">
      {block.parts ? renderInline(block.parts) : block.text}
    </p>
  );
}

function Subheading({ children }) {
  return (
    <h3 className="font-heading font-bold tracking-tight text-xl text-slate-900 mt-8 mb-3">
      {children}
    </h3>
  );
}

function UList({ items }) {
  return (
    <ul className="mb-4 space-y-2.5 pl-0">
      {items.map((it, i) => (
        <li
          key={i}
          className="flex items-start gap-3 text-[15px] leading-7 text-slate-700"
        >
          <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

function OList({ items }) {
  return (
    <ol className="mb-4 space-y-2.5 pl-0 counter-reset list-decimal">
      {items.map((it, i) => (
        <li
          key={i}
          className="flex items-start gap-3 text-[15px] leading-7 text-slate-700"
        >
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-900 text-white text-xs font-semibold shrink-0 mt-0.5">
            {i + 1}
          </span>
          <span>{it}</span>
        </li>
      ))}
    </ol>
  );
}

function Callout({ title, items }) {
  return (
    <div className="my-6 rounded-xl border border-slate-200 bg-slate-50/70 p-6">
      <div className="text-[10px] font-semibold tracking-[0.25em] uppercase text-slate-500 mb-4">
        {title}
      </div>
      <dl className="space-y-4">
        {items.map((it, i) => (
          <div key={i}>
            <dt className="text-sm font-semibold text-slate-900 mb-1">
              {it.label}
            </dt>
            <dd className="text-[14px] leading-6 text-slate-600">{it.text}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

const TONE_STYLES = {
  success: {
    border: "border-emerald-200",
    bg: "bg-emerald-50/40",
    tagBg: "bg-emerald-100 text-emerald-800",
    icon: CheckCircle2,
    iconCls: "text-emerald-600",
  },
  warning: {
    border: "border-amber-200",
    bg: "bg-amber-50/40",
    tagBg: "bg-amber-100 text-amber-800",
    icon: AlertTriangle,
    iconCls: "text-amber-600",
  },
  danger: {
    border: "border-red-200",
    bg: "bg-red-50/40",
    tagBg: "bg-red-100 text-red-700",
    icon: AlertOctagon,
    iconCls: "text-red-600",
  },
};

function Scenarios({ items }) {
  return (
    <div className="my-6 grid gap-4 sm:grid-cols-2">
      {items.map((s, i) => {
        const t = TONE_STYLES[s.tone] || TONE_STYLES.success;
        const Icon = t.icon;
        return (
          <div
            key={i}
            className={`rounded-xl border ${t.border} ${t.bg} p-5`}
            data-testid={`scenario-${s.tag.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <div className="flex items-start gap-3 mb-3">
              <Icon
                className={`w-5 h-5 ${t.iconCls} shrink-0 mt-0.5`}
                strokeWidth={2}
              />
              <div className="min-w-0">
                <span
                  className={`inline-block text-[10px] font-semibold tracking-[0.2em] uppercase px-2 py-0.5 rounded ${t.tagBg} mb-1.5`}
                >
                  {s.tag}
                </span>
                <h4 className="font-semibold text-slate-900 leading-snug">
                  {s.title}
                </h4>
              </div>
            </div>
            <p className="text-[14px] leading-6 text-slate-700">{s.text}</p>
          </div>
        );
      })}
    </div>
  );
}

function Checklist({ items }) {
  return (
    <div className="my-6 space-y-3">
      {items.map((it, i) => (
        <div
          key={i}
          className="flex items-start gap-4 rounded-lg border border-slate-200 bg-white p-4"
        >
          <div className="shrink-0 w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm font-semibold">
            {i + 1}
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-slate-900 mb-1 leading-snug">
              {it.title}
            </div>
            <div className="text-[14px] leading-6 text-slate-600">
              {it.parts ? renderInline(it.parts) : it.text}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function renderBlock(block, i) {
  switch (block.type) {
    case "p":
      return <Paragraph key={i} block={block} />;
    case "h3":
      return <Subheading key={i}>{block.text}</Subheading>;
    case "ul":
      return <UList key={i} items={block.items} />;
    case "ol":
      return <OList key={i} items={block.items} />;
    case "callout":
      return <Callout key={i} title={block.title} items={block.items} />;
    case "scenarios":
      return <Scenarios key={i} items={block.items} />;
    case "checklist":
      return <Checklist key={i} items={block.items} />;
    default:
      return null;
  }
}

export default function ManualPage() {
  return (
    <div className="max-w-4xl mx-auto pb-16" data-testid="manual-page">
      {/* Hero */}
      <div className="mb-10">
        <div className="text-xs font-medium tracking-[0.2em] text-slate-500 uppercase mb-3">
          Handleiding
        </div>
        <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl text-slate-900 leading-[1.05]">
          {MANUAL_TITLE}
        </h1>
        <p className="mt-4 text-base text-slate-600 leading-relaxed max-w-2xl">
          {MANUAL_INTRO}
        </p>
      </div>

      {/* Table of contents */}
      <div
        className="rounded-xl border border-slate-200 bg-slate-50/60 p-6 mb-12"
        data-testid="manual-toc"
      >
        <div className="flex items-center gap-2 text-[10px] font-semibold tracking-[0.25em] uppercase text-slate-500 mb-4">
          <ListChecks className="w-3.5 h-3.5" strokeWidth={2} />
          Inhoud
        </div>
        <ol className="grid sm:grid-cols-2 gap-x-8 gap-y-2.5">
          {MANUAL_SECTIONS.map((s, i) => (
            <li key={s.id} className="flex items-baseline gap-3">
              <span className="font-mono text-xs tabular-nums text-slate-400 w-6 shrink-0">
                {String(i + 1).padStart(2, "0")}
              </span>
              <a
                href={`#${s.id}`}
                className="text-sm text-slate-700 hover:text-slate-900 hover:underline underline-offset-4"
                data-testid={`toc-${s.id}`}
              >
                {s.heading}
              </a>
            </li>
          ))}
        </ol>
      </div>

      {/* Sections */}
      {MANUAL_SECTIONS.map((s, i) => (
        <section
          key={s.id}
          id={s.id}
          className="mb-16 scroll-mt-24"
          data-testid={`section-${s.id}`}
        >
          <div className="flex items-baseline gap-4 mb-6 pb-4 border-b border-slate-200">
            <span className="font-mono text-sm tabular-nums text-slate-400">
              {String(i + 1).padStart(2, "0")}
            </span>
            <h2 className="font-heading font-black tracking-tight text-2xl sm:text-3xl text-slate-900">
              {s.heading}
            </h2>
          </div>
          <div>{s.blocks.map((b, j) => renderBlock(b, j))}</div>
        </section>
      ))}

      {/* Footer / back to top */}
      <div className="text-center pt-8 border-t border-slate-200">
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="text-xs font-medium tracking-[0.2em] uppercase text-slate-500 hover:text-slate-900"
        >
          ↑ Terug naar boven
        </a>
      </div>
    </div>
  );
}
